import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { dashApi } from "@/lib/dashApi";
import { fmtValue } from "@/lib/format";
import { EmptyState, ErrorState, LoadingState, PageHeader, Panel, RiskBandPill, SourceBadge } from "@/components/dash/ui";

const DIMS = [
  { key: "company", label: "By company" },
  { key: "branch", label: "By branch" },
  { key: "source", label: "By source" },
];

function SummaryTable() {
  const [dim, setDim] = useState("company");
  const q = useQuery({ queryKey: ["pacing-summary", dim], queryFn: () => dashApi.pacingSummary(dim) });
  return (
    <Panel
      title="Company / branch / source pacing"
      subtitle="No per-entity target is configured — each row's band compares its projection to its OWN prior full month."
      right={
        <div className="flex gap-1">
          {DIMS.map((d) => (
            <button key={d.key} type="button" onClick={() => setDim(d.key)}
              className={`rounded-md px-2.5 py-1 text-xs ${dim === d.key ? "bg-brand-500/20 text-brand-200 ring-1 ring-brand-500/40" : "bg-ink-800 text-slate-400 hover:bg-ink-700"}`}>
              {d.label}
            </button>
          ))}
        </div>
      }
    >
      {q.isLoading ? <LoadingState /> : q.isError ? <ErrorState error={q.error} /> : !q.data?.rows.length ? (
        <EmptyState compact>No warehouse history for this dimension yet.</EmptyState>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wide text-slate-500">
              <th className="pb-2 font-medium">{q.data.dim === "source" ? "Metric" : q.data.label ?? q.data.dim}</th>
              <th className="pb-2 text-right font-medium">MTD</th>
              <th className="pb-2 text-right font-medium">Projected EOM</th>
              <th className="pb-2 text-right font-medium">Prior month</th>
              <th className="pb-2 text-right font-medium">vs prior</th>
              <th className="pb-2 text-right font-medium">Band</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ink-500/40">
            {q.data.rows.map((r) => (
              <tr key={r.name} className="text-slate-300">
                <td className="py-2">{r.name}{r.source && <span className="ml-2"><SourceBadge code={r.source} /></span>}</td>
                <td className="py-2 text-right tabular-nums text-white">{fmtValue(r.mtd_actual, r.unit ?? (q.data!.unit ?? "count"))}</td>
                <td className="py-2 text-right tabular-nums text-slate-200">{fmtValue(r.projected_eom, r.unit ?? (q.data!.unit ?? "count"))}</td>
                <td className="py-2 text-right tabular-nums text-slate-400">{fmtValue(r.prev_month, r.unit ?? (q.data!.unit ?? "count"))}</td>
                <td className="py-2 text-right tabular-nums text-slate-400">{r.vs_prev_pct === null ? "—" : `${r.vs_prev_pct > 0 ? "+" : ""}${r.vs_prev_pct}%`}</td>
                <td className="py-2 text-right"><RiskBandPill band={r.band} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {q.data?.note && <p className="mt-2 text-[11px] text-slate-600">{q.data.note}</p>}
    </Panel>
  );
}

export default function TargetPacingPage() {
  const q = useQuery({ queryKey: ["pacing"], queryFn: dashApi.pacing });
  const m = q.data?.month;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Target Pacing"
        description="Are we on track this month? Month-to-date actuals vs expected progress, the daily run-rate needed to hit target, and the projected end-of-month result."
        right={m && <span className="text-xs text-slate-500">{m.days_elapsed}/{m.days_in_month} days · {m.timezone}</span>}
      />
      <Panel title="This month vs target">
        {q.isLoading ? <LoadingState /> : q.isError ? <ErrorState error={q.error} /> : !q.data?.flow.length ? (
          <EmptyState>No pacing data available yet.</EmptyState>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wide text-slate-500">
                <th className="pb-2 font-medium">KPI</th>
                <th className="pb-2 text-right font-medium">MTD</th>
                <th className="pb-2 text-right font-medium">Expected</th>
                <th className="pb-2 text-right font-medium">Projected EOM</th>
                <th className="pb-2 text-right font-medium">Target</th>
                <th className="pb-2 text-right font-medium">Req/day</th>
                <th className="pb-2 text-right font-medium">% achieved</th>
                <th className="pb-2 text-right font-medium">Band</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-500/40">
              {q.data.flow.map((r) => (
                <tr key={r.key} className="text-slate-300">
                  <td className="py-2"><span className="text-slate-100">{r.label}</span> <SourceBadge code={r.source} /></td>
                  <td className="py-2 text-right tabular-nums text-white">{fmtValue(r.mtd_actual, r.unit)}</td>
                  <td className="py-2 text-right tabular-nums text-slate-400">{r.expected_progress === null ? "—" : fmtValue(r.expected_progress, r.unit)}</td>
                  <td className="py-2 text-right tabular-nums text-slate-200">{fmtValue(r.projected_eom, r.unit)}</td>
                  <td className="py-2 text-right tabular-nums text-slate-400">{r.has_target ? fmtValue(r.target, r.unit) : <span className="text-slate-600">no target</span>}</td>
                  <td className="py-2 text-right tabular-nums text-slate-400">{r.required_daily_run_rate === null ? "—" : fmtValue(r.required_daily_run_rate, r.unit)}</td>
                  <td className="py-2 text-right tabular-nums text-slate-400">{r.pct_achieved === null ? "—" : `${r.pct_achieved}%`}</td>
                  <td className="py-2 text-right"><RiskBandPill band={r.risk_band} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>
      {q.data?.sla.length ? (
        <Panel title="Service levels (point-in-time, lower is better)">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wide text-slate-500">
                <th className="pb-2 font-medium">KPI</th>
                <th className="pb-2 text-right font-medium">Current</th>
                <th className="pb-2 text-right font-medium">Target</th>
                <th className="pb-2 text-right font-medium">Band</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-500/40">
              {q.data.sla.map((r) => (
                <tr key={r.key} className="text-slate-300">
                  <td className="py-2"><span className="text-slate-100">{r.label}</span> <SourceBadge code={r.source} /></td>
                  <td className="py-2 text-right tabular-nums text-white">{r.actual === null ? "—" : fmtValue(r.actual, r.unit)}</td>
                  <td className="py-2 text-right tabular-nums text-slate-400">{r.has_target ? fmtValue(r.target, r.unit) : <span className="text-slate-600">no target</span>}</td>
                  <td className="py-2 text-right"><RiskBandPill band={r.risk_band} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
      ) : null}
      <SummaryTable />
    </div>
  );
}
